/**
 * LiveMarketTicker.jsx
 * Live streaming market ticker tape displaying real-time indices, crypto, and commodities.
 * Features live price changes, colored percentage badges, and 1-click chart inspection.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as apiClient from '../services/apiClient';

export default function LiveMarketTicker({ onSelectTicker }) {
  const [tickerItems, setTickerItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const lastFetchTimeRef = useRef(0);

  const fetchOverview = useCallback(async (force = false) => {
    // Only fetch if document is actively visible (or forced by user click)
    if (!force && document.visibilityState !== 'visible') return;

    const now = Date.now();
    if (!force && now - lastFetchTimeRef.current < 60000) return; // at most once a minute

    try {
      const data = await apiClient.request('/market/overview');
      if (Array.isArray(data) && data.length > 0) {
        setTickerItems(data);
        setLastUpdated(new Date());
        lastFetchTimeRef.current = now;
      }
    } catch (err) {
      console.warn('Could not fetch market overview:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview(true);

    // Auto-refresh every 90 seconds, only when tab is actively visible
    const interval = setInterval(() => {
      fetchOverview(false);
    }, 90000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchOverview(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [fetchOverview]);

  if (loading && tickerItems.length === 0) {
    return (
      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '6px 0', scrollbarWidth: 'none' }}>
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div
            key={i}
            style={{
              minWidth: '130px',
              height: '42px',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '10px',
              animation: 'pulse 1.5s infinite'
            }}
          />
        ))}
      </div>
    );
  }

  if (tickerItems.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', color: 'var(--text-muted)', padding: '0 2px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-cyan)', boxShadow: '0 0 8px var(--accent-cyan)' }} />
          <span style={{ fontWeight: '700', letterSpacing: '0.02em', textTransform: 'uppercase' }}>Live Market Pulse</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {lastUpdated && (
            <span>Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
          )}
          <button
            type="button"
            onClick={() => fetchOverview(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--accent-cyan)',
              cursor: 'pointer',
              fontSize: '11px',
              padding: '0 4px',
              opacity: 0.8
            }}
            title="Refresh Live Market Data"
          >
            🔄
          </button>
        </div>
      </div>

      <div
        className="hide-scrollbar"
        style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          padding: '4px 0 8px 0',
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {tickerItems.map(item => {
          const isPos = (item.changePct || 0) >= 0;
          const formattedPrice = item.price > 1000 
            ? item.price.toLocaleString(undefined, { maximumFractionDigits: 2 })
            : item.price.toFixed(item.price < 1 ? 4 : 2);

          return (
            <div
              key={item.symbol}
              onClick={() => onSelectTicker && onSelectTicker(item)}
              className="hover-card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: '10px',
                padding: '6px 10px',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.15s ease'
              }}
            >
              <span style={{ fontSize: '13px' }}>{item.icon}</span>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-main)' }}>
                    {item.label}
                  </span>
                  <span
                    style={{
                      fontSize: '9px',
                      fontWeight: '800',
                      color: isPos ? 'var(--color-inflow)' : 'var(--color-outflow)',
                      background: isPos ? 'rgba(0, 245, 212, 0.1)' : 'rgba(255, 71, 87, 0.1)',
                      padding: '1px 4px',
                      borderRadius: '4px'
                    }}
                  >
                    {isPos ? '+' : ''}{(item.changePct || 0).toFixed(2)}%
                  </span>
                </div>
                <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)' }}>
                  {item.currency === 'USD' ? '$' : `${item.currency} `}{formattedPrice}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
